import type { ComplianceReport, Obligation, Rule } from "../domain/compliance";
import type { DocumentDetail, DocumentRow, DocumentType } from "../domain/library";
import { TODAY } from "./clock";

/*
 * Sample compliance and library — the frameworks the Madinah build is
 * certified against (GACAR Part 139 v9.0, ICAO Annex 14, ISO 55001), a
 * month of daily and weekly obligations, the airports pack's rules with two
 * still gated, and a small controlled library with revisions, a review
 * falling due and one certificate expired. Labelled as a sample in the UI.
 */

function d(daysFromToday: number, hour = 6): string {
  const x = new Date(TODAY);
  x.setUTCDate(x.getUTCDate() + daysFromToday);
  x.setUTCHours(hour, 0, 0, 0);
  return x.toISOString();
}
function day(daysFromToday: number): string {
  return d(daysFromToday).slice(0, 10);
}

const OBLIGATIONS: Obligation[] = [];
/* Daily runway inspection — GACAR 139.327 — met every day for the last 30, today pending until the round completes. */
for (let i = 30; i >= 1; i--) {
  OBLIGATIONS.push({
    framework: "GACAR Part 139",
    title: "Daily movement-area serviceability inspection",
    period: "daily",
    periodKey: day(-i),
    status: "met",
    dueAt: d(-i, 23),
    metAt: d(-i, 4),
    evidenceCount: 1,
  });
}
OBLIGATIONS.push({
  framework: "GACAR Part 139",
  title: "Daily movement-area serviceability inspection",
  period: "daily",
  periodKey: day(0),
  status: "met",
  dueAt: d(0, 23),
  metAt: d(0, 4),
  evidenceCount: 1,
});
/* Weekly AGL night check — one missed week. */
for (let w = 4; w >= 0; w--) {
  const missed = w === 2;
  OBLIGATIONS.push({
    framework: "GACAR Part 139",
    title: "Weekly AGL night serviceability check",
    period: "weekly",
    periodKey: `2026-W${36 - w}`,
    status: missed ? "overdue" : w === 0 ? "pending" : "met",
    dueAt: d(-7 * w + 6, 23),
    metAt: missed || w === 0 ? undefined : d(-7 * w + 2, 22),
    evidenceCount: missed || w === 0 ? 0 : 1,
  });
}
OBLIGATIONS.push(
  {
    framework: "ICAO Annex 14",
    title: "Runway friction survey (continuous friction measuring equipment)",
    period: "quarterly",
    periodKey: "2026-Q3",
    status: "met",
    dueAt: d(24, 23),
    metAt: "2026-07-11T08:10:00Z",
    evidenceCount: 2,
  },
  {
    framework: "ICAO Annex 14",
    title: "Rubber removal where friction falls below the maintenance planning level",
    period: "event",
    periodKey: "FR-2026-07-11",
    status: "pending",
    dueAt: d(9, 23),
    evidenceCount: 0,
  },
  {
    framework: "ICAO Annex 14",
    title: "Obstacle limitation surfaces survey",
    period: "annual",
    periodKey: "2026",
    status: "pending",
    dueAt: d(62, 23),
    evidenceCount: 0,
  },
  {
    framework: "GACAR Part 139",
    title: "Aerodrome emergency exercise (partial)",
    period: "annual",
    periodKey: "2026",
    status: "overdue",
    dueAt: d(-18, 23),
    evidenceCount: 0,
  },
  {
    framework: "GACAR Part 139",
    title: "Wildlife hazard assessment review",
    period: "annual",
    periodKey: "2026",
    status: "met",
    dueAt: d(40, 23),
    metAt: d(-12, 10),
    evidenceCount: 1,
  },
  {
    framework: "ISO 55001",
    title: "Asset register condition review — pavements",
    period: "quarterly",
    periodKey: "2026-Q3",
    status: "met",
    dueAt: d(24, 23),
    metAt: d(-8, 9),
    evidenceCount: 3,
  },
  {
    framework: "ISO 55001",
    title: "Asset register condition review — AGL",
    period: "quarterly",
    periodKey: "2026-Q3",
    status: "pending",
    dueAt: d(24, 23),
    evidenceCount: 0,
  },
  {
    framework: "ISO 55001",
    title: "Management review of asset management objectives",
    period: "annual",
    periodKey: "2026",
    status: "waived",
    dueAt: d(-30, 23),
    evidenceCount: 0,
  },
);

function readiness(rows: Obligation[]) {
  const by = new Map<string, Obligation[]>();
  for (const o of rows) by.set(o.framework, [...(by.get(o.framework) ?? []), o]);
  return [...by.entries()]
    .map(([framework, xs]) => {
      const met = xs.filter((o) => o.status === "met").length;
      return {
        framework,
        total: xs.length,
        met,
        overdue: xs.filter((o) => o.status === "overdue").length,
        pct: Math.round((1000 * met) / xs.length) / 10,
      };
    })
    .sort((a, b) => a.framework.localeCompare(b.framework));
}

export const COMPLIANCE: ComplianceReport = { readiness: readiness(OBLIGATIONS), obligations: OBLIGATIONS };

export const RULES: Rule[] = [
  {
    key: "friction_below_minimum",
    name: "Minimum friction level",
    ruleClass: "regulatory",
    kind: "reactive",
    severity: 5,
    enabled: true,
    verifyRequired: true,
    parameters: { min_friction: 0.43, window: 3, device: "ASFT", speed_kmh: 65 },
    packDefaults: { min_friction: 0.42, window: 3, device: "ASFT", speed_kmh: 65 },
    confirmedAgainst: "Aerodrome Manual Part D §3.4 (GACAR 139.327), issue 9",
    verifiedAt: "2026-08-20T09:15:00Z",
    reference: "GACAR Part 139 §139.327; ICAO Annex 14 Vol I, Table A-1",
    trigger: "reading.survey",
  },
  {
    key: "agl_consecutive_outage",
    name: "Consecutive AGL unit outage",
    ruleClass: "safety",
    kind: "reactive",
    severity: 4,
    enabled: true,
    verifyRequired: false,
    parameters: { min_run: 2 },
    packDefaults: { min_run: 2 },
    reference: "ICAO Annex 14 Vol I, 10.5.7",
    trigger: "inspection.completed",
  },
  {
    key: "lamp_end_of_life",
    name: "Lamp end-of-life prediction",
    ruleClass: "predictive",
    kind: "predictive",
    severity: 3,
    enabled: true,
    verifyRequired: false,
    parameters: { life_fraction: 0.9, rated_hours: 1000 },
    packDefaults: { life_fraction: 0.9, rated_hours: 1000 },
    reference: "Manufacturer rated life; Aerodrome Manual Part E §4.5",
    trigger: "schedule.daily",
  },
  {
    key: "rubber_maintenance_level",
    name: "Rubber removal at the maintenance planning level",
    ruleClass: "regulatory",
    kind: "reactive",
    severity: 3,
    enabled: false,
    verifyRequired: true,
    parameters: { maintenance_level: 0.53 },
    packDefaults: { maintenance_level: 0.53 },
    reference: "ICAO Annex 14 Vol I, Attachment A §7; GACAR 139.327",
    trigger: "reading.survey",
    gatedReason: "thresholds must be confirmed against the certification basis",
  },
  {
    key: "pci_below_threshold",
    name: "Pavement condition index below rehabilitation threshold",
    ruleClass: "operational",
    kind: "reactive",
    severity: 3,
    enabled: false,
    verifyRequired: true,
    parameters: { pci_min: 55 },
    packDefaults: { pci_min: 55 },
    reference: "ASTM D5340; Aerodrome Manual Part E §3",
    trigger: "inspection.completed",
    gatedReason: "thresholds must be confirmed against the certification basis",
  },
  {
    key: "obligation_overdue",
    name: "Recurring obligation not met by its due date",
    ruleClass: "compliance",
    kind: "reactive",
    severity: 4,
    enabled: true,
    verifyRequired: false,
    parameters: { grace_hours: 0 },
    packDefaults: { grace_hours: 0 },
    trigger: "schedule.hourly",
  },
];

export const DOCUMENT_TYPES: DocumentType[] = [
  {
    key: "service_contract",
    name: "Service provider contract",
    category: "controlled",
    controlled: true,
    requiresApproval: true,
    expires: true,
    retentionYears: 10,
    defaultClassification: "restricted",
    pack: "airports",
  },
  {
    key: "manual",
    name: "Aerodrome manual part",
    category: "certification",
    controlled: true,
    requiresApproval: true,
    reviewMonths: 12,
    expires: false,
    retentionYears: 10,
    defaultClassification: "internal",
    pack: "airports",
  },
  {
    key: "procedure",
    name: "Operating procedure",
    category: "operations",
    controlled: true,
    requiresApproval: true,
    reviewMonths: 24,
    expires: false,
    retentionYears: 7,
    defaultClassification: "internal",
    pack: "airports",
  },
  {
    key: "certificate",
    name: "Certificate",
    /* the platform's category for every certificate type (packs/airports/library) */
    category: "certificate",
    controlled: true,
    requiresApproval: false,
    expires: true,
    retentionYears: 10,
    defaultClassification: "internal",
    pack: "airports",
  },
  {
    key: "drawing",
    name: "Drawing",
    category: "engineering",
    controlled: true,
    requiresApproval: true,
    expires: false,
    retentionYears: 25,
    defaultClassification: "internal",
    pack: "airports",
  },
  {
    key: "report",
    name: "Survey or inspection report",
    category: "records",
    controlled: false,
    requiresApproval: false,
    expires: false,
    retentionYears: 7,
    defaultClassification: "internal",
    pack: "airports",
  },
  {
    key: "security_programme",
    name: "Security programme",
    category: "security",
    controlled: true,
    requiresApproval: true,
    reviewMonths: 12,
    expires: false,
    retentionYears: 10,
    defaultClassification: "restricted",
    pack: "airports",
  },
];

type Seed = Omit<DocumentDetail, "revisionHistory" | "links" | "acknowledgements" | "revisions" | "createdAt"> & {
  createdAt?: string;
  history: {
    revision: string;
    status: DocumentDetail["revisionHistory"][number]["status"];
    issuedOn?: string;
    changeNote?: string;
    author?: string;
    approvedBy?: string;
  }[];
  links?: DocumentDetail["links"];
  acks?: DocumentDetail["acknowledgements"];
};

const SEEDS: Seed[] = [
  {
    id: "doc-am-a",
    title: "Aerodrome Manual — Part A: General",
    reference: "OEMA-AM-A",
    type: "manual",
    status: "issued",
    classification: "internal",
    tags: ["aerodrome-manual", "gacar-139"],
    revision: "Issue 9",
    issuedOn: "2026-02-01",
    reviewDueOn: "2027-02-01",
    revisionStatus: "issued",
    history: [
      {
        revision: "Issue 9",
        status: "issued",
        issuedOn: "2026-02-01",
        changeNote: "GACAR Part 139 v9.0 alignment; organisation chart updated.",
        author: "Safety & compliance",
        approvedBy: "Accountable manager",
      },
      {
        revision: "Issue 8",
        status: "superseded",
        issuedOn: "2025-02-01",
        changeNote: "Annual review.",
        author: "Safety & compliance",
        approvedBy: "Accountable manager",
      },
    ],
    acks: [
      {
        revision: "Issue 9",
        revisionStatus: "issued",
        person: "Duty manager A",
        required: true,
        acknowledgedAt: "2026-02-03T07:00:00Z",
        isCurrent: true,
      },
      {
        revision: "Issue 9",
        revisionStatus: "issued",
        person: "Duty manager B",
        required: true,
        acknowledgedAt: "2026-02-05T07:00:00Z",
        isCurrent: true,
      },
      { revision: "Issue 9", revisionStatus: "issued", person: "AGL supervisor", required: true, isCurrent: true },
      { revision: "Issue 9", revisionStatus: "issued", person: "Operator", required: true, isCurrent: true },
    ],
  },
  {
    id: "doc-am-d",
    title: "Aerodrome Manual — Part D: Operating procedures",
    reference: "OEMA-AM-D",
    type: "manual",
    status: "issued",
    classification: "internal",
    tags: ["aerodrome-manual", "gacar-139", "movement-area"],
    revision: "Issue 9",
    issuedOn: "2026-02-01",
    reviewDueOn: day(20),
    revisionStatus: "issued",
    history: [
      {
        revision: "Issue 9",
        status: "issued",
        issuedOn: "2026-02-01",
        changeNote: "Friction survey procedure rewritten around CFME; minimum friction level stated with its basis.",
        author: "Airside operations",
        approvedBy: "Accountable manager",
      },
      {
        revision: "Issue 8",
        status: "superseded",
        issuedOn: "2025-02-01",
        author: "Airside operations",
        approvedBy: "Accountable manager",
      },
    ],
    links: [{ entityKind: "rule", entityId: "friction_below_minimum", relation: "basis" }],
  },
  {
    id: "doc-am-e",
    title: "Aerodrome Manual — Part E: Aerodrome administration and safety management",
    reference: "OEMA-AM-E",
    type: "manual",
    status: "in_review",
    classification: "internal",
    tags: ["aerodrome-manual", "sms"],
    revision: "Issue 9",
    issuedOn: "2026-02-01",
    reviewDueOn: "2027-02-01",
    revisionStatus: "issued",
    history: [
      {
        revision: "Issue 10 (draft)",
        status: "in_review",
        changeNote: "Predictive maintenance programme added as §4.5; AGL lamp life policy.",
        author: "Engineering",
      },
      {
        revision: "Issue 9",
        status: "issued",
        issuedOn: "2026-02-01",
        author: "Safety & compliance",
        approvedBy: "Accountable manager",
      },
    ],
  },
  {
    id: "doc-proc-fric",
    title: "Runway friction survey procedure",
    reference: "OEMA-OP-014",
    type: "procedure",
    status: "issued",
    classification: "internal",
    tags: ["friction", "runway", "cfme"],
    revision: "C",
    issuedOn: "2026-03-15",
    reviewDueOn: "2028-03-15",
    revisionStatus: "issued",
    history: [
      {
        revision: "C",
        status: "issued",
        issuedOn: "2026-03-15",
        changeNote: "Survey speed fixed at 65 km/h; lane offsets 3 m and 6 m.",
        author: "Airside operations",
        approvedBy: "Head of airside",
      },
      {
        revision: "B",
        status: "superseded",
        issuedOn: "2024-06-01",
        author: "Airside operations",
        approvedBy: "Head of airside",
      },
      {
        revision: "A",
        status: "superseded",
        issuedOn: "2022-01-10",
        author: "Airside operations",
        approvedBy: "Head of airside",
      },
    ],
  },
  {
    id: "doc-cert-asft",
    title: "ASFT friction tester calibration certificate",
    reference: "Cal.Cert.SFT933",
    type: "certificate",
    status: "issued",
    classification: "internal",
    siteId: "site_oema",
    tags: ["calibration", "cfme"],
    revision: "2025",
    issuedOn: "2025-08-20",
    expiresOn: "2026-08-20",
    revisionStatus: "issued",
    history: [
      {
        revision: "2025",
        status: "issued",
        issuedOn: "2025-08-20",
        author: "Equipment",
        changeNote: "Annual calibration — SFT 933.",
      },
    ],
  },
  {
    id: "doc-cert-aero",
    title: "Aerodrome certificate",
    reference: "GACA-AC-OEMA",
    type: "certificate",
    status: "issued",
    classification: "internal",
    siteId: "site_oema",
    tags: ["gacar-139"],
    revision: "2024",
    issuedOn: "2024-11-01",
    expiresOn: "2027-10-31",
    revisionStatus: "issued",
    history: [{ revision: "2024", status: "issued", issuedOn: "2024-11-01", author: "Safety & compliance" }],
  },
  {
    id: "doc-drw-agl",
    title: "AGL layout — RWY 17/35 and taxiways",
    reference: "OEMA-E-AGL-001",
    type: "drawing",
    status: "issued",
    classification: "internal",
    siteId: "site_oema",
    tags: ["agl", "drawing"],
    revision: "P4",
    issuedOn: "2025-12-02",
    revisionStatus: "issued",
    history: [
      {
        revision: "P4",
        status: "issued",
        issuedOn: "2025-12-02",
        changeNote: "Holding position B1 units renumbered after circuit split.",
        author: "Engineering",
        approvedBy: "Head of engineering",
      },
      {
        revision: "P3",
        status: "superseded",
        issuedOn: "2023-05-14",
        author: "Engineering",
        approvedBy: "Head of engineering",
      },
    ],
    links: [{ entityKind: "map_layer", entityId: "agl", relation: "source" }],
  },
  {
    id: "doc-rep-fric",
    title: "Friction survey report FR-2026-07-11",
    reference: "FR-2026-07-11",
    type: "report",
    status: "issued",
    classification: "internal",
    siteId: "site_oema",
    tags: ["friction", "survey", "runway"],
    revision: "1",
    issuedOn: "2026-07-11",
    revisionStatus: "issued",
    history: [
      {
        revision: "1",
        status: "issued",
        issuedOn: "2026-07-11",
        author: "Airside operations",
        changeNote: "CH 1200–1500 below minimum on the +3 m lane; rubber removal raised.",
      },
    ],
    links: [
      { entityKind: "alert", entityId: "al-friction-17", relation: "evidence" },
      { entityKind: "work_order", entityId: "00000000-0000-4000-8000-000000000006", relation: "evidence" },
    ],
  },
  {
    id: "doc-sec",
    title: "Aerodrome security programme",
    reference: "OEMA-ASP",
    type: "security_programme",
    status: "issued",
    classification: "restricted",
    tags: ["security"],
    revision: "Issue 5",
    issuedOn: "2026-01-15",
    reviewDueOn: "2027-01-15",
    revisionStatus: "issued",
    history: [
      {
        revision: "Issue 5",
        status: "issued",
        issuedOn: "2026-01-15",
        author: "Security",
        approvedBy: "Accountable manager",
      },
    ],
  },
  {
    id: "doc-proc-old",
    title: "Apron sweeping schedule (superseded by OP-014 annex)",
    reference: "OEMA-OP-003",
    type: "procedure",
    status: "withdrawn",
    classification: "internal",
    tags: ["fod"],
    revisionStatus: "withdrawn",
    withdrawnAt: "2026-03-15T00:00:00Z",
    withdrawnReason: "Merged into the FOD programme annex of OP-014.",
    history: [{ revision: "A", status: "withdrawn", issuedOn: "2021-04-01", author: "Airside operations" }],
  },
];

export const DOCUMENTS: DocumentDetail[] = SEEDS.map((s, i) => {
  const { history, links, acks, ...rest } = s;
  return {
    ...rest,
    createdAt: s.createdAt ?? `2025-0${(i % 8) + 1}-10T08:00:00Z`,
    revisions: history.length,
    revisionHistory: history.map((h, j) => ({
      id: `${s.id}-r${history.length - j}`,
      revision: h.revision,
      sequence: history.length - j,
      status: h.status,
      issuedOn: h.issuedOn,
      changeNote: h.changeNote,
      createdAt: h.issuedOn ? `${h.issuedOn}T08:00:00Z` : d(-3),
      author: h.author,
      approvedBy: h.approvedBy,
      approvedAt: h.approvedBy && h.issuedOn ? `${h.issuedOn}T07:00:00Z` : undefined,
      sha256: `${s.id}${j}`.padEnd(16, "0"),
      bytes: 240_000 + i * 31_000 + j * 7_000,
      contentType: "application/pdf",
    })),
    links: links ?? [],
    acknowledgements: acks ?? [],
  };
});

export function documentRows(): DocumentRow[] {
  return DOCUMENTS.map(
    ({
      revisionHistory: _h,
      links: _l,
      acknowledgements: _a,
      ownerId: _o,
      withdrawnAt: _w,
      withdrawnReason: _r,
      retentionUntil: _u,
      attrs: _t,
      ...row
    }) => row,
  );
}
